/**
 * Textes du parcours signataire.
 *
 * Deux langues reelles pour l'instant (fr, en) et un repli EXPLICITE vers
 * le francais pour les autres. Le CDC prevoit 36 langues : les ajouter est
 * un travail de traduction humaine, pas de code - et surtout pas de
 * traduction automatique pour l'ecran de consentement, qui a une valeur
 * juridique.
 */

export type Lang = "fr" | "en";

export function resolveLang(langue: string | null | undefined): Lang {
  const code = (langue ?? "").slice(0, 2).toLowerCase();
  return code === "en" ? "en" : "fr";
}

/**
 * ⚠️ TEXTE DE CONSENTEMENT PROVISOIRE - A REMPLACER PAR LA VERSION DU
 * JURISTE AVANT TOUTE MISE EN PRODUCTION COMMERCIALE.
 *
 * C'est l'ecran juridiquement le plus sensible du produit : c'est lui qui
 * fonde la validite du consentement en eIDAS et en RGPD. Il a ete redige
 * ici par un developpeur, ce que le CDC exclut explicitement. Il est
 * volontairement isole dans cette constante pour etre remplace d'un seul
 * geste, sans toucher au reste du code.
 *
 * Ce qu'il doit imperativement continuer d'annoncer, quelle que soit la
 * redaction finale (le taire serait deloyal, et les donnees sont bel et
 * bien enregistrees par la piste d'audit) :
 *   - la date et l'heure,
 *   - l'adresse IP,
 *   - l'agent utilisateur (navigateur).
 */
export const CONSENT_TEXT_IS_PROVISIONAL = true;

const fr = {
  provisional: "Texte provisoire, en cours de validation juridique.",

  // ---------------------------------------------------------- ① lecture
  loading: "Chargement de votre document…",
  asksYouToSign: "vous demande de signer",
  // Titre d'onglet : « <client> — signature » (jamais le titre du document).
  onglet: "signature",
  documentLabel: "Document",
  readBeforeSigning: "Prenez connaissance du document avant de signer.",
  download: "Télécharger",
  print: "Imprimer",
  page: "Page",
  of: "sur",
  zoomIn: "Agrandir",
  zoomOut: "Réduire",
  fullscreen: "Plein écran",
  exitFullscreen: "Quitter le plein écran",
  continueToConsent: "Continuer",
  refuse: "Refuser de signer",
  expiresOn: "Ce lien expire le",
  signerRank: (n: number, total: number) => `Signataire ${n} sur ${total}`,

  // B-18 : blocage dur. Le compteur n'est pas decoratif - c'est lui qui
  // explique pourquoi le bouton est inactif. Un bouton desactive sans
  // raison visible se lit comme une panne.
  //
  // Franck, 21/10/2026 : chaque page doit avoir ete vue (le serveur aussi).
  pagesSeen: (vues: number, total: number) =>
    `${vues} page${vues > 1 ? "s" : ""} sur ${total} vue${vues > 1 ? "s" : ""}`,
  readAllPages: "Faites défiler chaque page du document pour pouvoir continuer.",
  allPagesSeen: "Vous avez vu toutes les pages du document.",
  afficherLaPage: (n: number) => `Afficher la page ${n}`,
  // B-23 : le signataire doit voir OU il signe, et QUOI y sera pose.
  reperes: {
    signature: "Votre signature",
    date: "Date de signature",
    heure: "Heure de signature",
    date_heure: "Date et heure de signature",
    nom: "Votre nom",
    email: "Votre adresse e-mail",
    texte: "Texte à saisir",
    mention: "Mention à recopier",
    case: "Case à cocher",
    liste: "Choix à faire",
    piece_jointe: "Pièce à joindre",
    date_saisie: "Date à indiquer",
    paraphe: "Votre paraphe",
    radio: "Option à choisir",
    texte_fixe: "Texte",
  },
  recopiez: "Recopiez :",
  saisieObligatoire: "Ce champ est obligatoire.",
  saisieEchec: "Enregistrement impossible, réessayez.",
  facultatif: "Champ facultatif : vous pouvez le laisser vide.",
  enregistrer: "Enregistrer",
  annuler: "Annuler",
  // Transmission de la signature (Franck, 15/10/2026) : la secretaire au patron.
  transmettre: "Transmettre à une autre personne",
  transmettreTitre: "Transmettre la signature",
  transmettreAide: "Indiquez la personne qui signera à votre place. Elle recevra un nouveau lien ; le vôtre ne "
    + "fonctionnera plus, et l'émetteur sera prévenu.",
  transmettreEmail: "Adresse e-mail",
  transmettreValider: "Transmettre",
  transmisTitre: "Signature transmise",
  transmisCorps: (nom: string, email: string) =>
    `${nom} (${email}) a reçu un lien pour signer. Ce lien-ci ne fonctionne plus.`,
  // Lot 2bis (15/10/2026) : prénom et nom séparés, mobile en SMS, demande à l'émetteur.
  transmettrePrenom: "Prénom",
  transmettreNomFamille: "Nom",
  transmettreIdentiteAide: "Écrits exactement comme sur sa pièce d'identité : sa vérification d'identité les comparera.",
  transmettreEmailAide: "Elle recevra son propre lien à cette adresse.",
  transmettreTelephone: "Numéro de mobile",
  transmettreTelephoneAide: "Elle recevra son code de signature par SMS. Format international, par exemple +33612345678.",
  transmettreDemanderNote: (client: string) =>
    `${client} validera la transmission avant l'envoi du nouveau lien. Vous serez prévenu par e-mail.`,
  transmettreDemander: "Envoyer la demande",
  transmettreIncomplet: (tel: boolean) => `Indiquez le prénom, le nom, une adresse e-mail valide${tel ? " et le numéro de mobile au format +33612345678" : ""}.`,
  transmettreImpossible: "Transmission impossible",
  transmettreInterdit: (client: string) =>
    `${client} n'autorise pas la transmission de cette signature. Si vous n'êtes pas la personne qui doit signer, `
    + `contactez ${client} : il pourra envoyer le document à la bonne personne.`,
  transmettreLimite: "Cette signature a déjà été transmise deux fois : elle ne peut plus l'être. Contactez l'émetteur si besoin.",
  retourAuDocument: "Retour au document",
  continuer: "Continuer",
  accueilAideCle: "Lisez le document jusqu'au bout, complétez vos emplacements, puis signez avec la clé gardée "
    + "dans votre téléphone (Face ID, empreinte ou code du téléphone).",
  // 20/10/2026 : sur ordinateur, la signature par clé se finit sur le téléphone.
  accueilCleOrdinateur: "Vous êtes sur un ordinateur : un QR code vous permettra de finir sur votre téléphone.",
  attenteTitre: (client: string) => `En attente de la validation de ${client}`,
  attenteTransmission: (nom: string, email: string) => `Vous avez demandé à transmettre la signature à ${nom} (${email}).`,
  attenteNom: (nom: string) => `Vous avez demandé à signer sous votre nom vérifié : ${nom}.`,
  attenteNote: "Cette page se met à jour toute seule. Vous recevrez aussi un e-mail dès la réponse.",
  attenteRefuseeTitre: "Demande refusée",
  attenteRefuseeTransmission: (client: string) =>
    `${client} a refusé la transmission. Vous restez la personne qui signe ce document.`,
  attenteRefuseeNom: (client: string) =>
    `${client} a refusé la correction du nom. Contactez ${client} pour qu'il corrige le nom indiqué sur le document.`,
  reperesCompte: (n: number) =>
    n > 1 ? `Vous signez à ${n} endroits de ce document, encadrés en jaune.`
          : "Vous signez à 1 endroit de ce document, encadré en jaune.",
  reperesSuivant: (i: number, n: number) => `Aller à mon emplacement suivant (${i}/${n})`,
  reperesCourt: (i: number, n: number) => `Où je signe ${i}/${n} ›`,
  // B-28 : on agit SUR le document (demande du porteur du 06/10/2026).
  cliquerLesCadres: "Cliquez sur chaque cadre jaune du document pour le compléter.",
  suivantChamp: (libelle: string, i: number, n: number) => `Suivant : ${libelle} (${i}/${n})`,
  restants: (n: number) => n > 1 ? `Encore ${n} emplacements à compléter` : "Encore 1 emplacement à compléter",
  tousPrets: "Tous vos emplacements sont complétés.",
  validesSur: (v: number, n: number) => `${v} validé${v > 1 ? "s" : ""} sur ${n}`,
  precedent: "Précédent",
  pagePrecedente: "Page précédente",
  pageSuivante: "Page suivante",
  suivant: "Suivant",
  prochainAValider: (libelle: string, page: number) => `À valider : ${libelle} · page ${page}`,
  parapherTout: (n: number) => `Parapher toutes les pages (${n})`,
  allerValider: "Aller à la validation",
  pageDuDoc: (p: number) => `page ${p}`,
  validerEtSigner: "Je valide, je signe et je reçois mon code",
  // Signature avancée par clé (lot 2bis) : pas de code, la clé signe.
  validerEtSignerCle: "Je valide et je signe avec ma clé",
  poursuivre: "Poursuivre",
  lireEtSigner: "Voir le document",
  accueilAide: "Lisez le document jusqu'au bout, complétez vos emplacements, puis validez avec le code reçu.",
  choisir: "Choisir…",
  pieceFormats: "PDF, JPEG ou PNG, 10 Mo au plus.",
  messageDe: (nom: string) => `Message de ${nom}`,
  refuserDeSigner: "Je refuse de signer",
  finDuDocument: "Fin du document",
  avantDeSigner: "Avant de signer",
  outils: "Outils",
  fermer: "Fermer",
  changerSignature: "Changer ma signature",
  signatureAChoisir: "Choisissez comment votre signature apparaîtra sur le document.",
  choisirSignature: "Choisir ma signature",
  viewerFailed:
    "Ce document ne peut pas être affiché ici. Téléchargez-le pour en prendre connaissance.",
  declareRead: "J'ai téléchargé le document et j'en ai pris connaissance.",
  declareRequired: "Veuillez confirmer avoir pris connaissance du document.",

  levels: {
    simple: "Signature électronique eIDAS.",
    // Franck, 02/11/2026 : vocabulaire du 01/11, sans promesse de valeur juridique.
    advanced: "Signature eIDAS Avancée AES.",
    qualified: "Signature eIDAS Qualifiée QES.",
    seal: "Cachet électronique du serveur, horodaté et archivé.",
  } satisfies Record<string, string>,


  // ------------------------------------------------ ②bis votre signature
  signatureTitle: "Votre signature",
  signatureLead: "Choisissez comment votre signature apparaîtra sur le document.",
  signatureByName: "Utiliser mon nom",
  signatureByNameHelp: "Votre nom, écrit dans la police manuscrite du document.",
  signatureByNamePlain: "Votre nom, tel qu'il apparaîtra sur le document.",
  signatureByDraw: "Tracer ma signature",
  signatureByDrawHelp: "Au doigt sur un écran tactile, à la souris sinon.",
  signatureByImage: "Importer une image",
  signatureByImageHelp: "Une photo ou un scan de votre signature (PNG ou JPEG, 4 Mo maximum).",
  signatureDrawHere: "Signez dans le cadre",
  signatureClear: "Effacer",
  signatureChoose: "Choisir une image",
  signatureSubmit: "Continuer",
  signatureDrawRequired: "Tracez votre signature pour continuer.",
  signatureImageRequired: "Choisissez une image pour continuer.",
  signatureNote:
    "Ce visuel représente votre signature ; c'est la validation du code reçu qui l'engage.",
  // Signature avancée par clé : c'est la clé qui engage, pas un code.
  signatureNoteCle: "Ce visuel représente votre signature ; c'est votre clé, utilisée au moment de signer, qui "
    + "vous engage.",
  // ------------------------------------------------------ ② consentement
  consentTitle: "Votre consentement",
  // Franck, 01/11/2026 : plus de « même valeur juridique qu'une signature
  // manuscrite » (formule interdite) ; repli du texte serveur 2026-11-01-bouton,
  // dont le corps est vide.
  consentBody: "",
  consentLogged:
    "Pour constituer la preuve de votre signature, nous enregistrons la date et l'heure, " +
    "votre adresse IP et votre navigateur. Ces informations figurent dans le dossier de " +
    "preuve remis avec le document signé.",
  consentCheckbox: "J'ai lu le document et j'accepte de le signer par voie électronique.",
  consentSubmit: "Je consens et je continue",
  consentRequired: "Veuillez cocher la case pour continuer.",

  // --------------------------------------------------------- ③ code
  otpTitle: "Vérification de votre identité",
  otpIntroEmail: "Nous allons envoyer un code à usage unique à l'adresse :",
  otpIntroSms: "Nous allons envoyer un code à usage unique au numéro :",
  otpSend: "Recevoir mon code",
  otpResend: "Renvoyer un code",
  otpResendIn: (s: number) => `Renvoyer un code dans ${s} s`,
  otpSentEmail: "Code envoyé par e-mail.",
  otpSentSms: "Code envoyé par SMS.",

  // --------------------------------------------------------- ④ saisie
  codeLabel: "Code à 6 chiffres",
  codeHelp: "Saisissez le code reçu.",
  codeSubmit: "Valider et signer",
  codeExpiresIn: (mmss: string) => `Code valable encore ${mmss}`,
  codeExpired: "Ce code a expiré. Demandez-en un nouveau.",
  attemptsLeft: (n: number) => (n > 1 ? `${n} tentatives restantes` : "Dernière tentative"),

  // ------------------------------------------------------ ⑤ confirmation
  doneTitle: "C'est signé.",
  doneBody:
    "Votre signature a été enregistrée. Le document signé est horodaté, et un dossier " +
    "de preuve retraçant chaque étape est conservé dix ans.",
  downloadSigned: "Télécharger le document signé",
  compteTitre: "Retrouvez tous vos documents signés",
  compteTexte: "Choisissez simplement un mot de passe : votre compte est créé avec votre adresse, et 5 € de crédit offert pour faire signer vos propres documents.",
  compteMdp: "Mot de passe (12 caractères au moins)",
  compteBouton: "Créer mon compte gratuit",
  compteGaranties: "Sans engagement · sans abonnement · sans carte bancaire",
  compteCree: "Votre compte est créé. Connectez-vous avec votre adresse et ce mot de passe.",
  compteDejaTitre: "Vous avez déjà un compte Sign365",
  compteDejaTexte: "Retrouvez ce document et tous vos documents signés dans votre espace.",
  compteConnexion: "Se connecter",
  compteEchec: "Le compte n'a pas pu être créé. Réessayez dans un instant.",
  downloadProof: "Télécharger le dossier de preuve",
  preparing: "Préparation du document signé…",
  preparingHint: "Cette étape prend quelques secondes.",
  waitingOthers:
    "Votre signature est enregistrée. Le document signé sera disponible quand tous les " +
    "signataires auront signé. Vous le recevrez par e-mail.",
  downloadFailed: "Le téléchargement a échoué. Réessayez dans quelques instants.",
  proofPreparing:
    "Dossier de preuve en cours de préparation. Il apparaîtra ici dès qu'il sera scellé, " +
    "en général en quelques minutes.",

  // ------------------------------------------------------------- refus
  declineTitle: "Refuser de signer",
  declineBody: "Vous pouvez indiquer un motif. Il sera transmis à l'émetteur.",
  declineReason: "Motif (facultatif)",
  declineConfirm: "Confirmer mon refus",
  declineCancel: "Revenir au document",
  declinedTitle: "Vous avez refusé de signer.",
  declinedBody: "L'émetteur en a été informé. Vous pouvez fermer cette page.",
  // Parcours clos par un autre que le signataire (06/10/2026).
  closedTitle: {
    cancelled: "Cette demande de signature a été annulée par l'émetteur.",
    declined: "Cette demande de signature a été refusée par un autre signataire.",
    expired: "Cette demande de signature a expiré.",
  } as Record<string, string>,
  closedBody: "Le document ne peut plus être signé. Pour toute question, contactez l'émetteur.",

  // ------------------------------------------------------------ erreurs
  errors: {
    not_found:
      "Ce lien de signature est inconnu ou a été révoqué. Contactez la personne qui vous l'a envoyé.",
    expired:
      "Ce lien de signature a expiré. Demandez-en un nouveau à la personne qui vous l'a envoyé.",
    rate_limited:
      "Trop de demandes de code. Patientez quelques minutes, puis réessayez.",
    channel_unavailable:
      "Nous ne parvenons pas à vous envoyer votre code. Contactez la personne qui vous a envoyé ce document : vous ne pouvez pas corriger ce point vous-même.",
    invalid_code: "Ce code est incorrect.",
    code_expired:
      "Ce code n'est plus valable. Demandez-en un nouveau à l'aide du bouton ci-dessous.",
    no_code: "Aucun code n'a été demandé. Demandez-en un pour continuer.",
    conflict: "Cette étape a déjà été effectuée.",
    partial_review:
      "Toutes les pages du document n'ont pas encore été affichées. Parcourez-les pour continuer.",
    review_required: "Affichez d'abord le document en entier.",
    signature_invalide: "Ce visuel n'a pas pu être utilisé. Réessayez avec un tracé ou une autre image.",
    signature_requise: "Choisissez d'abord votre visuel de signature.",
    network: "Connexion perdue. Vérifiez votre accès à Internet, puis réessayez.",
    server: "Une erreur est survenue de notre côté. Réessayez dans un instant.",
  } satisfies Record<string, string>,

  retry: "Réessayer",
  needHelp: "Un problème ? Contactez la personne qui vous a envoyé ce document.",
};

type Dict = typeof fr;

const en: Dict = {
  provisional: "Provisional text, pending legal review.",

  loading: "Loading your document…",
  asksYouToSign: "asks you to sign",
  onglet: "signature",
  documentLabel: "Document",
  readBeforeSigning: "Please read the document before signing.",
  download: "Download",
  print: "Print",
  page: "Page",
  of: "of",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  fullscreen: "Full screen",
  exitFullscreen: "Exit full screen",
  continueToConsent: "Continue",
  refuse: "Decline to sign",
  expiresOn: "This link expires on",
  signerRank: (n: number, total: number) => `Signer ${n} of ${total}`,

  pagesSeen: (vues: number, total: number) =>
    `${vues} of ${total} page${total > 1 ? "s" : ""} viewed`,
  readAllPages: "Please scroll through every page of the document to continue.",
  allPagesSeen: "You have viewed every page of the document.",
  afficherLaPage: (n: number) => `Show page ${n}`,
  reperes: {
    signature: "Your signature",
    date: "Signing date",
    heure: "Signing time",
    date_heure: "Signing date and time",
    nom: "Your name",
    email: "Your email address",
    texte: "Text to enter",
    mention: "Statement to copy",
    case: "Checkbox",
    liste: "Choice to make",
    piece_jointe: "File to attach",
    date_saisie: "Date to enter",
    paraphe: "Your initials",
    radio: "Option to choose",
    texte_fixe: "Text",
  },
  recopiez: "Copy exactly:",
  saisieObligatoire: "This field is required.",
  saisieEchec: "Could not save, please try again.",
  facultatif: "Optional field: you may leave it empty.",
  enregistrer: "Save",
  annuler: "Cancel",
  transmettre: "Pass on to someone else",
  transmettreTitre: "Pass on the signature",
  transmettreAide: "Enter the person who will sign instead of you. They will receive a new link; yours will no "
    + "longer work, and the issuer will be informed.",
  transmettreEmail: "Email address",
  transmettreValider: "Pass on",
  transmisTitre: "Signature passed on",
  transmisCorps: (nom: string, email: string) =>
    `${nom} (${email}) has received a link to sign. This link no longer works.`,
  transmettrePrenom: "First name",
  transmettreNomFamille: "Last name",
  transmettreIdentiteAide: "Exactly as on their identity document: their identity check will compare them.",
  transmettreEmailAide: "They will receive their own link at this address.",
  transmettreTelephone: "Mobile number",
  transmettreTelephoneAide: "They will receive their signing code by text message. International format, e.g. +447700900123.",
  transmettreDemanderNote: (client: string) =>
    `${client} will approve the hand-over before the new link is sent. You will be notified by email.`,
  transmettreDemander: "Send the request",
  transmettreIncomplet: (tel: boolean) => `Enter the first name, last name, a valid email address${tel ? " and the mobile number in +447700900123 format" : ""}.`,
  transmettreImpossible: "Hand-over not possible",
  transmettreInterdit: (client: string) =>
    `${client} does not allow this signature to be passed on. If you are not the person who should sign, `
    + `contact ${client}: they can send the document to the right person.`,
  transmettreLimite: "This signature has already been passed on twice and cannot be passed on again. Contact the issuer if needed.",
  retourAuDocument: "Back to the document",
  continuer: "Continue",
  accueilAideCle: "Read the document to the end, complete your places, then sign with the key kept "
    + "on your phone (Face ID, fingerprint or phone passcode).",
  accueilCleOrdinateur: "You are on a computer: a QR code will let you finish on your phone.",
  attenteTitre: (client: string) => `Waiting for approval from ${client}`,
  attenteTransmission: (nom: string, email: string) => `You asked to pass the signature on to ${nom} (${email}).`,
  attenteNom: (nom: string) => `You asked to sign under your verified name: ${nom}.`,
  attenteNote: "This page updates by itself. You will also receive an email as soon as they answer.",
  attenteRefuseeTitre: "Request declined",
  attenteRefuseeTransmission: (client: string) =>
    `${client} declined the hand-over. You remain the person who signs this document.`,
  attenteRefuseeNom: (client: string) =>
    `${client} declined the name correction. Contact ${client} so they correct the name shown on the document.`,
  reperesCompte: (n: number) =>
    n > 1 ? `You sign in ${n} places in this document, outlined in yellow.`
          : "You sign in 1 place in this document, outlined in yellow.",
  reperesSuivant: (i: number, n: number) => `Go to my next place (${i}/${n})`,
  reperesCourt: (i: number, n: number) => `Where I sign ${i}/${n} ›`,
  cliquerLesCadres: "Click each yellow box in the document to complete it.",
  suivantChamp: (libelle: string, i: number, n: number) => `Next: ${libelle} (${i}/${n})`,
  restants: (n: number) => n > 1 ? `${n} places left to complete` : "1 place left to complete",
  tousPrets: "All your places are complete.",
  validesSur: (v: number, n: number) => `${v} of ${n} done`,
  precedent: "Previous",
  pagePrecedente: "Previous page",
  pageSuivante: "Next page",
  suivant: "Next",
  prochainAValider: (libelle: string, page: number) => `To do: ${libelle} · page ${page}`,
  parapherTout: (n: number) => `Initial all pages (${n})`,
  allerValider: "Go to validation",
  pageDuDoc: (p: number) => `page ${p}`,
  validerEtSigner: "I approve, I sign and I get my code",
  validerEtSignerCle: "I approve and sign with my key",
  poursuivre: "Continue",
  lireEtSigner: "View the document",
  accueilAide: "Read the document to the end, complete your places, then confirm with the code you receive.",
  choisir: "Choose…",
  pieceFormats: "PDF, JPEG or PNG, 10 MB at most.",
  messageDe: (nom: string) => `Message from ${nom}`,
  refuserDeSigner: "I decline to sign",
  finDuDocument: "End of document",
  avantDeSigner: "Before signing",
  outils: "Tools",
  fermer: "Close",
  changerSignature: "Change my signature",
  signatureAChoisir: "Choose how your signature will appear on the document.",
  choisirSignature: "Choose my signature",
  viewerFailed: "This document cannot be displayed here. Download it to read it.",
  declareRead: "I have downloaded the document and read it.",
  declareRequired: "Please confirm that you have read the document.",

  levels: {
    simple: "eIDAS electronic signature.",
    advanced: "eIDAS Advanced Electronic Signature (AES).",
    qualified: "eIDAS Qualified Electronic Signature (QES).",
    seal: "Server electronic seal, timestamped and archived.",
  },


  signatureTitle: "Your signature",
  signatureLead: "Choose how your signature will appear on the document.",
  signatureByName: "Use my name",
  signatureByNameHelp: "Your name, in the document's handwriting font.",
  signatureByNamePlain: "Your name, as it will appear on the document.",
  signatureByDraw: "Draw my signature",
  signatureByDrawHelp: "With your finger on a touchscreen, with the mouse otherwise.",
  signatureByImage: "Upload an image",
  signatureByImageHelp: "A photo or scan of your signature (PNG or JPEG, 4 MB maximum).",
  signatureDrawHere: "Sign inside the frame",
  signatureClear: "Clear",
  signatureChoose: "Choose an image",
  signatureSubmit: "Continue",
  signatureDrawRequired: "Please draw your signature to continue.",
  signatureImageRequired: "Please choose an image to continue.",
  signatureNote:
    "This visual represents your signature; it is the code you confirm that commits you.",
  signatureNoteCle: "This visual represents your signature; it is your key, used when you sign, that commits you.",
  consentTitle: "Your consent",
  consentBody: "",
  consentLogged:
    "To establish proof of your signature, we record the date and time, your IP address " +
    "and your browser. This information appears in the evidence package provided with " +
    "the signed document.",
  consentCheckbox: "I have read the document and agree to sign it electronically.",
  consentSubmit: "I consent and continue",
  consentRequired: "Please tick the box to continue.",

  otpTitle: "Identity verification",
  otpIntroEmail: "We will send a one-time code to:",
  otpIntroSms: "We will send a one-time code to:",
  otpSend: "Send my code",
  otpResend: "Send a new code",
  otpResendIn: (s: number) => `Send a new code in ${s}s`,
  otpSentEmail: "Code sent by email.",
  otpSentSms: "Code sent by SMS.",

  codeLabel: "6-digit code",
  codeHelp: "Enter the code you received.",
  codeSubmit: "Confirm and sign",
  codeExpiresIn: (mmss: string) => `Code valid for another ${mmss}`,
  codeExpired: "This code has expired. Please request a new one.",
  attemptsLeft: (n: number) => (n > 1 ? `${n} attempts remaining` : "Last attempt"),

  doneTitle: "Signed.",
  doneBody:
    "Your signature has been recorded. The signed document is timestamped, and an " +
    "evidence package tracing every step is kept for ten years.",
  downloadSigned: "Download the signed document",
  compteTitre: "Keep all your signed documents",
  compteTexte: "Just choose a password: your account is created with your address, with €5 of credit to get your own documents signed.",
  compteMdp: "Password (at least 12 characters)",
  compteBouton: "Create my free account",
  compteGaranties: "No commitment · no subscription · no credit card",
  compteCree: "Your account is ready. Sign in with your address and this password.",
  compteDejaTitre: "You already have a Sign365 account",
  compteDejaTexte: "Find this document and all your signed documents in your space.",
  compteConnexion: "Sign in",
  compteEchec: "The account could not be created. Please try again in a moment.",
  downloadProof: "Download the evidence package",
  preparing: "Preparing the signed document…",
  preparingHint: "This takes a few seconds.",
  waitingOthers:
    "Your signature has been recorded. The signed document will be available once all " +
    "signers have signed. You will receive it by e-mail.",
  downloadFailed: "The download failed. Please try again in a few moments.",
  proofPreparing:
    "The evidence package is being prepared. It will appear here as soon as it is sealed, " +
    "usually within a few minutes.",

  declineTitle: "Decline to sign",
  declineBody: "You may give a reason. It will be sent to the issuer.",
  declineReason: "Reason (optional)",
  declineConfirm: "Confirm my refusal",
  declineCancel: "Back to the document",
  declinedTitle: "You have declined to sign.",
  declinedBody: "The issuer has been notified. You may close this page.",
  closedTitle: {
    cancelled: "This signature request has been cancelled by the issuer.",
    declined: "This signature request has been declined by another signer.",
    expired: "This signature request has expired.",
  } as Record<string, string>,
  closedBody: "The document can no longer be signed. For any question, please contact the issuer.",

  errors: {
    not_found: "This signing link is unknown or has been revoked. Please contact whoever sent it to you.",
    expired: "This signing link has expired. Please ask whoever sent it for a new one.",
    rate_limited: "Too many code requests. Please wait a few minutes and try again.",
    channel_unavailable:
      "We cannot send you your code. Please contact whoever sent you this document: this is not something you can fix yourself.",
    invalid_code: "This code is incorrect.",
    code_expired: "This code is no longer valid. Request a new one using the button below.",
    no_code: "No code has been requested. Request one to continue.",
    conflict: "This step has already been completed.",
    partial_review: "Some pages of the document have not been displayed yet. Please go through them.",
    review_required: "Please display the whole document first.",
    signature_invalide: "This signature could not be used. Try drawing it again, or another image.",
    signature_requise: "Please choose your signature first.",
    network: "Connection lost. Check your internet access and try again.",
    server: "Something went wrong on our side. Please try again shortly.",
  },

  retry: "Try again",
  needHelp: "Having trouble? Contact whoever sent you this document.",
};

const dictionaries: Record<Lang, Dict> = { fr, en };

/**
 * B-13 (Franck, 20/10/2026) : l'approbateur valide SANS signer - aucun texte
 * de son parcours ne lui parle de signature. Seuls les textes qui le disaient
 * changent ; tout le reste vient du dictionnaire commun. Le texte d'engagement
 * enregistre, lui, vient du serveur (`consentement`) : consentCheckbox n'est
 * qu'un repli.
 */
const approbation: Record<Lang, (d: Dict) => Partial<Dict>> = {
  fr: (d) => ({
    asksYouToSign: "vous demande d'approuver",
    onglet: "approbation",
    readBeforeSigning: "Prenez connaissance du document avant de l'approuver.",
    refuse: "Refuser d'approuver",
    signerRank: (n: number, total: number) => `Étape ${n} sur ${total}`,
    transmettreTitre: "Transmettre l'approbation",
    transmettreAide: "Indiquez la personne qui approuvera à votre place. Elle recevra un nouveau lien ; le vôtre ne "
      + "fonctionnera plus, et l'émetteur sera prévenu.",
    transmisTitre: "Approbation transmise",
    transmisCorps: (nom: string, email: string) =>
      `${nom} (${email}) a reçu un lien pour approuver. Ce lien-ci ne fonctionne plus.`,
    transmettreTelephoneAide: "Elle recevra son code par SMS. Format international, par exemple +33612345678.",
    transmettreInterdit: (client: string) =>
      `${client} n'autorise pas la transmission de cette approbation. Si vous n'êtes pas la personne qui doit `
      + `approuver, contactez ${client} : il pourra envoyer le document à la bonne personne.`,
    transmettreLimite: "Cette approbation a déjà été transmise deux fois : elle ne peut plus l'être. Contactez l'émetteur si besoin.",
    attenteTransmission: (nom: string, email: string) => `Vous avez demandé à transmettre l'approbation à ${nom} (${email}).`,
    attenteRefuseeTransmission: (client: string) =>
      `${client} a refusé la transmission. Vous restez la personne qui approuve ce document.`,
    reperesCompte: (n: number) =>
      n > 1 ? `${n} emplacements à compléter dans ce document, encadrés en jaune.`
            : "1 emplacement à compléter dans ce document, encadré en jaune.",
    reperesCourt: (i: number, n: number) => `Emplacement ${i}/${n} ›`,
    validerEtSigner: "J'approuve et je reçois mon code",
    accueilAide: "Lisez le document jusqu'au bout, puis approuvez-le avec le code reçu.",
    refuserDeSigner: "Je refuse d'approuver",
    avantDeSigner: "Avant d'approuver",
    consentBody: "",
    consentLogged:
      "Pour constituer la preuve de votre approbation, nous enregistrons la date et l'heure, votre adresse IP "
      + "et votre navigateur. Ces informations figurent dans le dossier de preuve remis avec le document final.",
    consentCheckbox: "J'ai lu le document et je l'approuve.",
    codeSubmit: "Valider et approuver",
    doneTitle: "Vous avez approuvé ce document.",
    // Niveau de SIGNATURE (écran de repli) : sans objet pour lui.
    levels: { simple: "", advanced: "", qualified: "", seal: "" },
    compteTitre: "Retrouvez tous vos documents",
    compteTexte: "Choisissez simplement un mot de passe : votre compte est créé avec votre adresse, et 5 € de crédit offert pour envoyer vos propres documents.",
    compteDejaTexte: "Retrouvez ce document et tous vos documents dans votre espace.",
    doneBody: "Votre approbation a été enregistrée et horodatée. Un dossier de preuve retraçant chaque étape "
      + "est conservé dix ans.",
    waitingOthers: "Votre approbation est enregistrée. Le document final sera disponible quand tous les "
      + "signataires auront signé. Vous le recevrez par e-mail.",
    declineTitle: "Refuser d'approuver",
    declinedTitle: "Vous avez refusé d'approuver.",
    closedTitle: {
      cancelled: "Cette demande d'approbation a été annulée par l'émetteur.",
      declined: "Cette demande d'approbation a été refusée par une autre personne.",
      expired: "Cette demande d'approbation a expiré.",
    },
    closedBody: "Le document ne peut plus être approuvé. Pour toute question, contactez l'émetteur.",
    errors: {
      ...d.errors,
      not_found: "Ce lien d'approbation est inconnu ou a été révoqué. Contactez la personne qui vous l'a envoyé.",
      expired: "Ce lien d'approbation a expiré. Demandez-en un nouveau à la personne qui vous l'a envoyé.",
    },
  }),
  en: (d) => ({
    asksYouToSign: "asks you to approve",
    onglet: "approval",
    readBeforeSigning: "Please read the document before approving it.",
    refuse: "Decline to approve",
    signerRank: (n: number, total: number) => `Step ${n} of ${total}`,
    transmettreTitre: "Pass on the approval",
    transmettreAide: "Enter the person who will approve instead of you. They will receive a new link; yours will no "
      + "longer work, and the issuer will be informed.",
    transmisTitre: "Approval passed on",
    transmisCorps: (nom: string, email: string) =>
      `${nom} (${email}) has received a link to approve. This link no longer works.`,
    transmettreTelephoneAide: "They will receive their code by text message. International format, e.g. +447700900123.",
    transmettreInterdit: (client: string) =>
      `${client} does not allow this approval to be passed on. If you are not the person who should approve, `
      + `contact ${client}: they can send the document to the right person.`,
    transmettreLimite: "This approval has already been passed on twice and cannot be passed on again. Contact the issuer if needed.",
    attenteTransmission: (nom: string, email: string) => `You asked to pass the approval on to ${nom} (${email}).`,
    attenteRefuseeTransmission: (client: string) =>
      `${client} declined the hand-over. You remain the person who approves this document.`,
    reperesCompte: (n: number) =>
      n > 1 ? `${n} places to complete in this document, outlined in yellow.`
            : "1 place to complete in this document, outlined in yellow.",
    reperesCourt: (i: number, n: number) => `Place ${i}/${n} ›`,
    validerEtSigner: "I approve and I get my code",
    accueilAide: "Read the document to the end, then approve it with the code you receive.",
    refuserDeSigner: "I decline to approve",
    avantDeSigner: "Before approving",
    consentBody: "",
    consentLogged:
      "To establish proof of your approval, we record the date and time, your IP address and your browser. "
      + "This information appears in the evidence package provided with the final document.",
    consentCheckbox: "I have read the document and I approve it.",
    codeSubmit: "Confirm and approve",
    doneTitle: "You have approved this document.",
    // Niveau de SIGNATURE (écran de repli) : sans objet pour lui.
    levels: { simple: "", advanced: "", qualified: "", seal: "" },
    compteTitre: "Keep all your documents",
    compteTexte: "Just choose a password: your account is created with your address, with €5 of credit to send your own documents.",
    compteDejaTexte: "Find this document and all your documents in your space.",
    doneBody: "Your approval has been recorded and timestamped. An evidence package tracing every step is kept "
      + "for ten years.",
    waitingOthers: "Your approval has been recorded. The final document will be available once all signers "
      + "have signed. You will receive it by e-mail.",
    declineTitle: "Decline to approve",
    declinedTitle: "You have declined to approve.",
    closedTitle: {
      cancelled: "This approval request has been cancelled by the issuer.",
      declined: "This approval request has been declined by someone else.",
      expired: "This approval request has expired.",
    },
    closedBody: "The document can no longer be approved. For any question, please contact the issuer.",
    errors: {
      ...d.errors,
      not_found: "This approval link is unknown or has been revoked. Please contact whoever sent it to you.",
      expired: "This approval link has expired. Please ask whoever sent it for a new one.",
    },
  }),
};

export function t(lang: Lang, role?: string | null): Dict {
  const d = dictionaries[lang];
  return role === "approver" ? { ...d, ...approbation[lang](d) } : d;
}
